<template>
  <div class="side-nav-wrapper">
    <!-- 侧边栏主体，当折叠时整体滑出屏幕，只保留一个小按钮 -->
    <div
      class="side-nav"
      :class="{ collapsed: collapsed }"
    >
      <div class="nav-header">
        <h3>管理后台</h3>
      </div>
      
      <el-menu
        :default-active="activeMenu"
        router
        class="admin-menu"
      >
        <el-menu-item index="/admin/dashboard">
          <el-icon><DataBoard /></el-icon>
          <template #title>
            概览
          </template>
        </el-menu-item>
        <el-menu-item index="/admin/challenges">
          <el-icon><List /></el-icon>
          <template #title>
            挑战管理
          </template>
        </el-menu-item>
      
        <el-menu-item index="/admin/announcements">
          <el-icon><ChatDotRound /></el-icon>
          <template #title>
            公告管理
          </template>
        </el-menu-item>
      
        <el-menu-item index="/admin/tags">
          <el-icon><PriceTag /></el-icon>
          <template #title>
            标签管理（暂无作用）
          </template>
        </el-menu-item>
      
        <el-menu-item index="/admin/learning">
          <el-icon><FolderOpened /></el-icon>
          <template #title>
            参考资料
          </template>
        </el-menu-item>

        <el-menu-item
          v-if="isSuperAdmin"
          index="/admin/invites"
        >
          <el-icon><Operation /></el-icon>
          <template #title>
            管理员邀请码
          </template>
        </el-menu-item>
      
        <el-sub-menu index="/admin/kb">
          <template #title>
            <el-icon><Collection /></el-icon>
            <span>知识库管理</span>
          </template>
          <el-menu-item index="/admin/kb/categories">
            分类管理
          </el-menu-item>
          <el-menu-item index="/admin/kb/series">
            专题管理
          </el-menu-item>
          <el-menu-item index="/admin/kb/articles">
            文章管理
          </el-menu-item>
        </el-sub-menu>
      
        <el-menu-item index="/admin/judge-jobs">
          <el-icon><Operation /></el-icon>
          <template #title>
            评测队列
          </template>
        </el-menu-item>
      
        <el-menu-item index="/admin/feedback">
          <el-icon><ChatDotRound /></el-icon>
          <template #title>
            反馈管理
          </template>
        </el-menu-item>
        <el-menu-item index="/admin/leaderboard">
          <el-icon><DataLine /></el-icon>
          <template #title>
            排行榜管理
          </template>
        </el-menu-item>
        <el-menu-item index="/admin/audit-logs">
          <el-icon><Document /></el-icon>
          <template #title>
            操作日志
          </template>
        </el-menu-item>
        <el-menu-item index="/admin/users">
          <el-icon><User /></el-icon>
          <template #title>
            用户管理
          </template>
        </el-menu-item>
        <el-menu-item index="/admin/suspicious-users">
          <el-icon><WarningFilled /></el-icon>
          <template #title>
            嫌疑用户
          </template>
        </el-menu-item>
        <el-menu-item
          v-if="isSuperAdmin"
          index="/admin/shop"
        >
          <el-icon><ShoppingBag /></el-icon>
          <template #title>
            商城管理
          </template>
        </el-menu-item>
      </el-menu>
    </div>

    <!-- 小型悬浮折叠/展开按钮 -->
    <button
      type="button"
      class="side-nav-toggle"
      :style="{ left: toggleLeft + 'px', top: toggleTop + 'px' }"
      @click="handleToggleClick"
      @mousedown.prevent="onDragStart"
      @touchstart.prevent="onDragStart"
    >
      <el-icon>
        <component :is="collapsed ? Expand : Fold" />
      </el-icon>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, inject, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import {
  Fold,
  Expand,
  DataBoard,
  List,
  PriceTag,
  FolderOpened,
  Collection,
  Operation,
  ChatDotRound,
  DataLine,
  Document,
  User,
  ShoppingBag,
  WarningFilled
} from '@element-plus/icons-vue'

const route = useRoute()
const collapsed = ref(false)
const authStore = useAuthStore()

// 同步收起状态到布局，使主内容区在收起时左移
const adminSidebarCollapsed = inject('adminSidebarCollapsed', null)
watch(collapsed, (v) => {
  if (adminSidebarCollapsed) adminSidebarCollapsed.value = v
}, { immediate: true })

// 悬浮球位置（相对于视口）
const toggleLeft = ref(8)
const toggleTop = ref(88)

let dragging = false
let dragStartX = 0
let dragStartY = 0
let startLeft = 0
let startTop = 0
// 标记当前这次交互中是否发生了位移，用于区分“点击”和“拖动”
let dragMoved = false
// 标记是否为触摸操作（手机端 touchstart 会 preventDefault，导致 click 不触发，需在 touchend 里触发展开/收起）
let isTouchDrag = false
// 触摸已在 onDragEnd 里触发了收起/展开时，避免随后可能触发的 click 再执行一次
let justToggledByTouch = false

const updatePositionWithinViewport = () => {
  if (typeof window === 'undefined') return
  const padding = 4
  const width = window.innerWidth || document.documentElement.clientWidth || 1920
  const height = window.innerHeight || document.documentElement.clientHeight || 1080
  const size = 44

  if (toggleLeft.value < padding) toggleLeft.value = padding
  if (toggleLeft.value > width - size - padding) toggleLeft.value = width - size - padding
  if (toggleTop.value < 72 + padding) toggleTop.value = 72 + padding
  if (toggleTop.value > height - size - padding) toggleTop.value = height - size - padding
}

const onDragMove = (event) => {
  if (!dragging || typeof window === 'undefined') return
  const point = event.touches ? event.touches[0] : event
  const deltaX = point.clientX - dragStartX
  const deltaY = point.clientY - dragStartY

  // 如果移动距离超过一个很小的阈值，就认为是“拖动”，后续点击不触发展开/收起
  if (!dragMoved && (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3)) {
    dragMoved = true
  }

  toggleLeft.value = startLeft + deltaX
  toggleTop.value = startTop + deltaY
  updatePositionWithinViewport()
}

const onDragEnd = () => {
  if (!dragging) return
  const wasTouch = isTouchDrag
  dragging = false
  isTouchDrag = false
  if (typeof window !== 'undefined') {
    window.removeEventListener('mousemove', onDragMove)
    window.removeEventListener('mouseup', onDragEnd)
    window.removeEventListener('touchmove', onDragMove)
    window.removeEventListener('touchend', onDragEnd)
    window.removeEventListener('touchcancel', onDragEnd)
  }
  // 手机端：touchstart 已 preventDefault，click 不会触发，在 touchend 时执行展开/收起
  if (wasTouch && !dragMoved) {
    collapsed.value = !collapsed.value
    justToggledByTouch = true
  }
}

const onDragStart = (event) => {
  if (typeof window === 'undefined') return
  dragging = true
  dragMoved = false
  isTouchDrag = !!(event.touches && event.touches.length > 0)
  const point = event.touches ? event.touches[0] : event
  dragStartX = point.clientX
  dragStartY = point.clientY
  startLeft = toggleLeft.value
  startTop = toggleTop.value

  window.addEventListener('mousemove', onDragMove)
  window.addEventListener('mouseup', onDragEnd)
  window.addEventListener('touchmove', onDragMove)
  window.addEventListener('touchend', onDragEnd)
  window.addEventListener('touchcancel', onDragEnd)
}

// 区分拖拽和点击：在 onDragStart 里只标记拖拽，真正的展开/收起通过单独的点击方法触发
const handleToggleClick = () => {
  // 手机端已在 touchend 里触发展开/收起，避免重复
  if (justToggledByTouch) {
    justToggledByTouch = false
    return
  }
  // 刚刚发生过拖动：这次 mouseup 触发的 click 不视为“点击”
  if (dragMoved) {
    dragMoved = false
    return
  }
  collapsed.value = !collapsed.value
}

onMounted(() => {
  updatePositionWithinViewport()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updatePositionWithinViewport)
  }
})

onBeforeUnmount(() => {
  onDragEnd()
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updatePositionWithinViewport)
  }
})

const activeMenu = computed(() => route.path)
const isSuperAdmin = computed(() => authStore.isSuperAdmin && authStore.isSuperAdmin())
</script>

<style scoped lang="scss">
.side-nav-wrapper {
  position: fixed;
  left: 0;
  top: 72px; // TopNav 高度是 72px
  z-index: 999;
}

.side-nav {
  width: 240px;
  background: var(--color-bg-card);
  border-right: 1px solid var(--color-border);
  height: calc(100vh - 72px);
  transition: transform var(--transition-base) var(--ease-out);
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  &.with-background {
    background: rgba(21, 29, 46, 0.94);
    backdrop-filter: blur(10px);
  }

  &.collapsed {
    transform: translateX(-100%);
  }
}

/* 亮色模式：侧栏统一白底 */
body[data-theme='light'] .side-nav {
  background: #ffffff !important;
  border-right-color: var(--color-border);
}
body[data-theme='light'] .side-nav.with-background {
  background: #ffffff !important;
  backdrop-filter: none;
}
body[data-theme='light'] .side-nav-toggle {
  background: #ffffff !important;
  border-color: var(--color-border);
  color: var(--color-text-secondary);
}
body[data-theme='light'] .side-nav-toggle:hover {
  background: var(--color-bg-hover) !important;
  color: var(--color-primary);
}

.nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 8px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0; // 防止被压缩
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
  }
  
  .collapse-btn {
    color: var(--color-text-secondary);
  }
}

.admin-menu {
  border: none;
  flex: 1; // 占据剩余空间
  overflow-y: auto;
  overflow-x: hidden;

  :deep(.el-menu) {
    border-right: none;
    background-color: transparent;
    padding: 8px 10px 16px;
  }

  :deep(.el-menu-item),
  :deep(.el-sub-menu__title) {
    height: 44px;
    line-height: 44px;
    margin: 2px 0;
    border-radius: 999px;
    padding-left: 14px !important;
    padding-right: 14px !important;
    color: var(--color-text-secondary);
    transition: background-color var(--transition-fast) var(--ease-out),
                color var(--transition-fast) var(--ease-out),
                transform var(--transition-fast) var(--ease-out),
                box-shadow var(--transition-fast) var(--ease-out);
  }

  :deep(.el-menu-item .el-icon),
  :deep(.el-sub-menu__title .el-icon) {
    margin-right: 8px;
    font-size: 18px;
  }

  :deep(.el-menu-item:hover),
  :deep(.el-sub-menu__title:hover) {
    background: var(--color-primary-50);
    color: var(--color-primary);
    transform: translateX(1px);
  }

  :deep(.el-menu-item.is-active) {
    background: linear-gradient(90deg, var(--color-primary-100), var(--color-primary-50));
    color: var(--color-primary);
    font-weight: 600;
    box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.25);
  }

  :deep(.el-menu-item.is-active .el-icon) {
    color: var(--color-primary);
  }
}

.side-nav-toggle {
  position: fixed;
  z-index: 1000;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-card);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: var(--shadow-md);
  color: var(--color-text-secondary);
  padding: 0;

  &:hover {
    background: var(--color-bg-hover);
    color: var(--color-primary);
  }

  @media (max-width: 1024px) {
    top: 80px;
  }
}
</style>
